import { P } from "pino";

// Misc endpoints for dynamic API (warehouses, suppliers)
export const MISC_API = {
  WAREHOUSES: '/api/warehouses',
  SUPPLIERS: '/api/suppliers',
  VENDORS: '/api/vendors',
  SALES_ORDERS: '/api/sales-orders',
  // Sequence utilities
  SEQUENCE_NEXT_AVAILABLE: '/api/misc/sequences/next',
  // Environment information
  ENVIRONMENT: '/api/misc/environment',
  // Feature flags
  FEATURE_FLAGS: '/api/misc/feature-flags',
  FEATURE_FLAG_EVALUATE: '/api/misc/feature-flags/evaluate',
  FEATURE_FLAG_BATCH: '/api/misc/feature-flags/batch',
};

// Authentication related API endpoints
export const AUTH_API = {
  SESSION: '/api/auth/session',
  ENHANCE_SESSION: '/api/auth/enhance-session',
};

// Navigation related API endpoints
export const NAVIGATION_API = {
  LEFT_MENU: '/api/navigation/left-menu',
};

// Centralized page route paths used by navigation
export const PAGE_PATHS = {
  STOCKS: '/Stock/',
  MASTERS: '/brands',
  PRODUCTS: '/products',
  SALES: '/dashboard/sales',
  SALES_ORDERS: '/sales-orders',
  PURCHASE_ORDERS: '/po',
  WAREHOUSES_TRANSFER: '/warehouse-transfer',
  WAREHOUSES: '/warehouses'
};
// Navigation helper for print barcode page. Accepts batchNumber and an optional
// external report base URL which will be URL-encoded and passed as a query param.
export const PRINT_NAV = {
  PAGE: (batchNumber: string | number, reportBaseUrl?: string) =>
    reportBaseUrl + `/Report/Index/11?uid=printbarcodes&batchnumber=${encodeURIComponent(String(batchNumber))}`,
};

// Cached reports base URL for this runtime (client or server). This prevents
// recomputing/parsing NEXTAUTH_URL or window.hostname on every call.
let cachedReportsBase: string | undefined;

// Compute (and cache) the reports service base URL for this runtime.
function computeReportsBase(): string {
  if (cachedReportsBase) return cachedReportsBase;

  const localhostBase = 'http://localhost:5287';

  // Detect localhost first (client or server via NEXTAUTH_URL)
  const isLocalHost = (() => {
    if (typeof window !== 'undefined') {
      return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    }
    const nextAuthUrl = process.env.NEXTAUTH_URL;
    return !!(nextAuthUrl && (nextAuthUrl.includes('localhost') || nextAuthUrl.includes('127.0.0.1')));
  })();

  if (isLocalHost) {
    cachedReportsBase = localhostBase;
    return cachedReportsBase;
  }

  // Resolve domain from client window or NEXTAUTH_URL on server
  let baseDomain = 'easybizgrow.com'; // fallback domain
  if (typeof window !== 'undefined') {
    try {
      const hostname = window.location.hostname || '';
      const parts = hostname.split('.');
      if (parts.length >= 2) baseDomain = parts.slice(-2).join('.');
    } catch (err) {
      // swallow; fallback domain will be used
    }
  } else {
    const nextAuthUrl = process.env.NEXTAUTH_URL;
    if (nextAuthUrl) {
      try {
        const url = new URL(nextAuthUrl);
        const parts = url.hostname.split('.');
        if (parts.length >= 2) baseDomain = parts.slice(-2).join('.');
      } catch (error) {
        // keep fallback
        // eslint-disable-next-line no-console
        console.warn('Failed to parse NEXTAUTH_URL:', error);
      }
    }
  }

  cachedReportsBase = `https://reports.${baseDomain}`;
  return cachedReportsBase;
}

// Carton UI routes
export const CARTON_PATHS = {
  LIST: '/cartons',
  ADDEDIT: '/cartons/addeditcarton'
};

// Centralized API endpoint constants for the project
export const PROM_API_URL = "/api/prom";

export const WAREHOUSES_API = {
  BASE: '/api/warehouses',
  BY_ID: (id: number | string) => `/api/warehouses/${id}`,
  LIST: '/api/warehouses',
};

export const CATEGORY_API = {
  BASE: '/api/categories',
  BY_ID: (id: number | string) => `/api/categories/${id}`,
  LIST: '/api/categories',
};

export const CARTON_API = {
  BASE: '/api/cartons',
  BY_ID: (id: number | string) => `/api/cartons/${id}`,
  LIST: '/api/cartons',
  FINISHED_PRODUCTS: '/api/cartons/finished-products',
};

export const PLACEHOLDER_API = {
  URL: (w: number | string, h: number | string) => `/api/placeholder/${w}/${h}`
};


export const STOCKIN_TRANSFER_API = {
  BASE: '/api/stock/in/transfer',
  BY_ID: (id: number | string) => `/api/stock/in/transfer/${id}`,
};
export const STOCKIN = {
  BASE: '/stock/in',
  ADDEDIT: '/stock/in/ae',
};

export const STOCKIN_GATEPASS_API = {
  BASE: '/api/stock/in/gatepass',
  BY_ID: (id: number | string) => `/api/stock/in/gatepass/${id}`,
};

export const STOCKIN_API = {
  BASE: '/api/stock/in',
  LIST: '/api/stock/in',
  BY_ID: (id: number | string) => `/api/stock/in/${id}`,
  ADDEDIT: '/api/stock/in/ae',
};

/**
 * Generate the print barcode URL for external reports service
 * Extracts domain from NEXTAUTH_URL and creates reports subdomain
 * @param batchNumber - The batch number to print barcodes for
 * @returns string - The complete URL to the reports service
 */
// Generic getter for the reports service base URL (cached).
export function getReportsBaseUrl(): string {
  return computeReportsBase();
}

// Returns both the base URL and the specific barcode report URL for a batch.
export function getReportsLinks(batchNumber: string | number): { baseUrl: string; barcodeUrl: string } {
  const base = computeReportsBase();
  const barcodeUrl = `${base}/Report/Index/11?uid=printbarcodes&batchnumber=${encodeURIComponent(String(batchNumber))}`;
  return { baseUrl: base, barcodeUrl };
}

// Backwards-compatible alias for callers that previously used getPrintBarcodeUrl
export const getPrintBarcodeUrl = getReportsLinks;

// Centralized API endpoint constants for product variant operations
export const VARIANT_API = {
  BASE: '/api/prodvar',
  BY_ID: (id: number | string) => `/api/prodvar/${id}`
};

export const BARCODE_API = {
  BASE: '/api/prodvar',
  BY_ID: (id: number | string) => `/api/prodvar/${id}`
};

export const STOCKOUT_API = {
  BASE: '/api/stock/out',
  LIST: '/api/stock/out',
  BY_ID: (id: number | string) => `/api/stock/out/${id}`,
  GATEPASS: '/api/stock/out/gatepass',
  TRANSFER: '/api/stock/out/transfer', // New warehouse transfer API using stock out process
};
export const STOCKOUT = {
  BASE: '/stock/out',
  ADDEDIT: '/stock/out/ae',
};

export const WAREHOUSE_TRANSFER_API = {
  BASE: '/api/warehouse-transfer',
  LIST: '/api/warehouse-transfer',
  BY_ID: (id: number | string) => `/api/warehouse-transfer/${id}`,
  AVAILABLE_BARCODES: '/api/warehouse-transfer/available-barcodes',
};
export const WAREHOUSE_TRANSFER = {
  BASE: '/warehouse-transfer',
  ADDEDIT: '/warehouse-transfer/ae',
  LIST: '/warehouse-transfer',
  DETAIL: (id: number | string) => `/warehouse-transfer/${id}`,
};

export const PRODUCTS_API = {
  BASE: '/api/products',
  BY_ID: (id: number | string) => `/api/products/${id}`,
  LIST: '/api/products',
  BULK_INSERT: '/api/products/BulkInsert',
};

export const CUSTOMERS_API = {
  BASE: '/api/customers',
  BY_ID: (id: number | string) => `/api/customers/${id}`,
  LIST: '/api/customers',
};

export const SUPPLIERS_API = {
  BASE: '/api/suppliers',
  BY_ID: (id: number | string) => `/api/suppliers/${id}`,
  LIST: '/api/suppliers',
};

export const VENDORS_API = {
  BASE: '/api/vendors',
  BY_ID: (id: number | string) => `/api/vendors/${id}`,
  LIST: '/api/vendors',
};

export const PURCHASE_ORDERS_API = {
  BASE: '/api/purchase-orders',
  BY_ID: (id: number | string) => `/api/purchase-orders/${id}`,
  LIST: '/api/purchase-orders',
  PRODUCTS: '/api/purchase-orders/products',
};

export const SALES_ORDERS_API = {
  BASE: '/api/sales-orders',
  PENDING: '/api/sales-orders/pending',
  QUICK: '/api/sales-orders/quick',
};

export const UNITS_API = {
  BASE: '/api/units',
};

export const COLOR_API = {
  BASE: '/api/colors',
  BY_ID: (id: number | string) => `/api/colors/${id}`,
  LIST: '/api/colors',
};

export const SIZE_API = {
  BASE: '/api/sizes',
  BY_ID: (id: number | string) => `/api/sizes/${id}`,
  LIST: '/api/sizes',
};

export const VARBARCODE_API = {
  BASE: '/api/varbarcode',
};

export const BRAND_API = {
  BASE: '/api/brands',
  BY_ID: (id: number | string) => `/api/brands/${id}`,
  LIST: '/api/brands',
};

